"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { OrderListItem,PAYMENT_METHOD, IS_DEBIT, ORDER_STATUS_LABELS, ORDER_STATUS, STATUS_BADGE_STYLES, REWASH_REASON_OPTIONS, } from "../services/order-service";
import OrderProgressBar from "./OrderProgressBar";
import { formatServerDateTime } from "../utils/datetime";
import ImageCustom from "../../../components/custom/image-custom";
import EquipmentService, { EQUIPMENT_TYPE_NAME, OrderEquipmentSummaryItem } from "../../equipment/services/equipment-service";
import { useFetchData } from "../../../hooks/useFetch";
import { ApiConfig, API_ORDER_ROUTER, API_FAULT_TICKET_ROUTER } from "../../../services/api-config";
import OrderProcessTab from "./OrderProcessTab";
import ReconcilePrintSheet from "./ReconcilePrintSheet";
import OrderTransferSection from "./OrderTransferSection";
import { isSuccess } from "../../../models/response/base-response";
import { FAULT_STATUS } from "../../fault-ticket/services/fault-ticket-service";
import { PickupStaff } from "../../../components/custom/pickup-staff-select";
import SelectDropdownAsyncPaginate from "../../../components/custom/select-dropdown-async-paginate";
import { API_USER_ROUTER } from "../../../services/api-config";
import FaultTicketCreateModal from "../../fault-ticket/components/fault-ticket-create-modal";
import { showToast } from "../../../components/custom/custom-toast";
import { useOrderRealtime } from "../../../hooks/useSocket";
import socketService, { OrderEventData } from "../../../services/socket.service";
import { useBranchContext } from "../../../contexts/BranchContext";
import { usePermission } from "../../../hooks/usePermission";
import { PERMISSION_CODE } from "../../../constants/permission";
import OrderItemsEditModal from "./OrderItemsEditModal";
import ChangePaymentMethodModal from "./ChangePaymentMethodModal";
import CancelCashDepositModal from "./CancelCashDepositModal";
import StepPhotosEditModal from "./StepPhotosEditModal";
import EditFinalWeightModal from "./EditFinalWeightModal";
import { ArrowLeftRight, Pencil, Plus, Undo2 } from "lucide-react";
import { formatKg } from "../../../utils/helpers";

// Trong chi tiết đơn chỉ hiện phiếu lỗi ĐANG CHỜ (chờ giải trình) hoặc ĐÃ CHỐT (đã ghi lỗi).
// Bỏ phiếu "Đã giải trình" (trung gian) và "Hủy lỗi".
const SHOWN_FAULT_STATUSES = [FAULT_STATUS.OPEN, FAULT_STATUS.RESOLVED];

// Bước ảnh sửa được trên web (key = trạng thái nơi chụp trong caption_images), theo thứ tự luồng.
const EDITABLE_PHOTO_STEPS: { key: number; label: string; countingOnly?: boolean }[] = [
  { key: 9, label: "Ảnh kiểm đếm (khi nhận)", countingOnly: true },
  { key: 2, label: "Ảnh lấy hàng" },
  { key: 18, label: "Ảnh kiểm đếm giao", countingOnly: true },
  { key: 5, label: "Ảnh giao hàng" },
];

interface OrderDetailModalProps {
  open: boolean;
  order: OrderListItem | null;
  onClose: () => void;
  onEditWeights?: () => void;
  onSplitOrder?: () => void;
  onViewSubOrder?: (subOrderId: number) => void;
  onViewParentOrder?: (parentOrderId: number) => void;
  onUpdateNote?: (orderId: number, note: string) => Promise<void>;
  onReload?: () => void;
  /** Nạp lại chi tiết đơn đang mở (sau khi sửa SL / đơn giá) — không chỉ reload danh sách. */
  onRefreshDetail?: (orderId: number) => void | Promise<void>;
}

const formatCurrency = (value: any): string => {
  const num = Number(value || 0);
  return num.toLocaleString("vi-VN", { minimumFractionDigits: 0 });
};

// 4 chế độ kiểm đếm/phân loại (mỗi mode = 1 lần đếm). Theo thứ tự luồng.
type ClassifyMode = 'counting' | 'pickup_check' | 'inspect' | 'transfer_check' | 'production_done' | 'delivery_pack' | 'delivery_check';
const CLASSIFY_MODE_ORDER: ClassifyMode[] = ['counting', 'pickup_check', 'inspect', 'transfer_check', 'production_done', 'delivery_pack', 'delivery_check'];
// Các mode ghi nhận theo TỪNG TÚI (mỗi túi = 1 log mang equipment_id)
const PER_BAG_MODES: ClassifyMode[] = ['pickup_check', 'delivery_pack'];
const CLASSIFY_MODE_META: Record<ClassifyMode, { label: string; cls: string }> = {
  counting: { label: '🧮 Kiểm đếm (với khách)', cls: 'bg-violet-100 text-violet-700' },
  pickup_check: { label: '🚚 Khi nhận (tài xế)', cls: 'bg-amber-100 text-amber-700' },
  inspect: { label: '🏭 Kiểm tra (CH)', cls: 'bg-green-100 text-green-700' },
  transfer_check: { label: '🔄 Đối chiếu (nơi nhận)', cls: 'bg-orange-100 text-orange-700' },
  production_done: { label: '🧺 Hoàn thành giặt', cls: 'bg-yellow-100 text-yellow-800' },
  delivery_pack: { label: '👜 Đóng túi giao', cls: 'bg-teal-100 text-teal-700' },
  delivery_check: { label: '📦 Kiểm đếm giao', cls: 'bg-blue-100 text-blue-700' },
};

const OrderDetailModal: React.FC<OrderDetailModalProps> = ({ open, order, onClose, onEditWeights, onSplitOrder, onViewSubOrder, onViewParentOrder, onUpdateNote, onReload, onRefreshDetail }) => {
  // ✅ Gọi useMemo và useState TRƯỚC khi kiểm tra return null
  const [editingNote, setEditingNote] = useState(false);
  const [noteValue, setNoteValue] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [activeTab, setActiveTab] = useState<"info" | "process">("info");
  const [showEditItems, setShowEditItems] = useState(false);
  const { hasPermission } = usePermission();
  // Sửa SL / đơn giá dòng vật tư — CHỈ quyền ORDER_ITEM_QUANTITY_EDIT (giám sát không được),
  // và không cho sửa đơn đã huỷ.
  const canEditItems =
    hasPermission([PERMISSION_CODE.ORDER_ITEM_QUANTITY_EDIT]) &&
    Number((order as any)?.status) !== ORDER_STATUS.CANCELED;
  // Đổi hình thức thanh toán sau hoàn tất — cùng quyền; QR đã thanh toán thì khoá (BE cũng chặn).
  const [showChangePayment, setShowChangePayment] = useState(false);
  const canChangePaymentMethod =
    canEditItems &&
    Number((order as any)?.status) === ORDER_STATUS.COMPLETE &&
    !(Number((order as any)?.payment_method) === PAYMENT_METHOD.QR_CODE && Number((order as any)?.payment_status) === 1);
  // "Huỷ SQTM" — quyền DRIVER_CASH_DEPOSIT_CANCEL, đơn đã hoàn tất. Không lọc theo hình thức hiện tại: đơn đã đổi
  // sang công nợ trước đây (khoản thu huỷ tay, lần nộp còn) cũng cần dùng. Modal tự kiểm tra còn gì để huỷ không.
  const [showCancelCashDeposit, setShowCancelCashDeposit] = useState(false);
  const canCancelCashDeposit =
    hasPermission([PERMISSION_CODE.DRIVER_CASH_DEPOSIT_CANCEL]) &&
    Number((order as any)?.status) === ORDER_STATUS.COMPLETE;
  // Sửa ảnh xác nhận các bước — quyền ORDER_PHOTO_EDIT, mọi trạng thái trừ huỷ (BE chặn thêm theo bước).
  const canEditPhotos =
    hasPermission([PERMISSION_CODE.ORDER_PHOTO_EDIT]) &&
    Number((order as any)?.status) !== ORDER_STATUS.CANCELED;
  const [editingPhotoKey, setEditingPhotoKey] = useState<number | null>(null);
  // Ảnh theo bước (caption_images[status] = chuỗi | mảng) → mảng URL.
  const captionMap = useMemo<Record<string, string[]>>(() => {
    const raw = (order as any)?.caption_images;
    if (!raw) return {};
    try {
      const obj = typeof raw === "string" ? JSON.parse(raw) : raw;
      const out: Record<string, string[]> = {};
      Object.entries(obj || {}).forEach(([k, v]) => {
        out[k] = Array.isArray(v) ? (v as string[]).filter(Boolean) : v ? [v as string] : [];
      });
      return out;
    } catch {
      return {};
    }
  }, [order]);
  // Bước được sửa: đơn đang chạy → chỉ bước đã có ảnh; đơn hoàn thành → thêm được bước còn thiếu
  // (ảnh kiểm đếm chỉ có ở đơn kiểm đếm). Khớp quy tắc BE `updateStepPhotos`.
  const editablePhotoSteps = useMemo(() => {
    if (!canEditPhotos) return [] as { key: number; label: string; urls: string[] }[];
    const isComplete = Number((order as any)?.status) === ORDER_STATUS.COMPLETE;
    const isCounting = Number((order as any)?.requires_counting) === 1;
    return EDITABLE_PHOTO_STEPS
      .filter((s) => !s.countingOnly || isCounting)
      .map((s) => ({ key: s.key, label: s.label, urls: captionMap[String(s.key)] || [] }))
      .filter((s) => s.urls.length > 0 || isComplete);
  }, [canEditPhotos, captionMap, order]);
  const items =useMemo(() => ((order as any)?.items ?? []) as any[], [order]);
  // Sửa sản lượng đơn ĐÃ HOÀN TẤT — cùng quyền sửa SL/đơn giá. Ẩn khi:
  //  - đơn cha có đơn con (sản lượng = tổng đơn con → sửa từng đơn con);
  //  - đơn tính tiền toàn bộ theo kg (sản lượng = SL tính tiền → sửa qua "Chỉnh SL / đơn giá"). BE cũng chặn.
  const [showEditFinalWeight, setShowEditFinalWeight] = useState(false);
  const canEditFinalWeight = useMemo(() => {
    if (!canEditItems || Number((order as any)?.status) !== ORDER_STATUS.COMPLETE) return false;
    if (Number((order as any)?.has_sub_orders) === 1) return false;
    const services = items.filter((it) => Number(it.status) !== 8 && Number(it.item_type) === 1);
    const allKg =
      services.length > 0 &&
      services.every((it) => Number(it.measure_type) === 2 && String(it.unit_name || "").trim().toLowerCase() === "kg");
    return !allKg;
  }, [canEditItems, order, items]);
  const totalQuantity = useMemo(
    () => items.reduce((sum, it) => sum + Number(it.quantity || 0), 0),
    [items]
  );
  // Tiền hàng = Σ(SL thực tế × đơn giá) trên từng dòng — đúng công thức duy nhất của hệ thống mới.
  // final_weight KHÔNG tham gia (xem product/docs/vat-tu-va-tinh-tien-ky-thuat.md §4).
  const lineGross = useMemo(
    () =>
      items.reduce(
        (sum, it) =>
          sum + (Number(it.total_price) || Number(it.price) * Number(it.quantity) || 0),
        0
      ),
    [items]
  );
  // Đơn vị chung của mọi dòng tính tiền (nếu có) — để footer "Tổng tiền hàng" hiện đúng đơn vị
  // thay vì "món". Đơn trộn đơn vị (kg + cái) → không hiện số, chỉ "Tổng tiền hàng:".
  const commonUnit = useMemo(() => {
    const units = Array.from(
      new Set(
        items
          .filter((it) => Number(it.status) !== 8)
          .map((it) => String(it.unit_name || "").trim().toLowerCase())
          .filter(Boolean)
      )
    );
    return units.length === 1 ? units[0] : null;
  }, [items]);

  // Re-wash hook
  const { request: postReWash } = useFetchData.Post<any>({
    projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
  });
  const reWashRef = useRef(postReWash);
  useEffect(() => { reWashRef.current = postReWash; }, [postReWash]);

  // Modal "Giặt lại": phát hiện NGAY (đồ ở shop, miễn phí) hoặc SAU (lấy hàng lại, có thể tính phí)
  const [showReWash, setShowReWash] = useState(false);
  const [reWashReason, setReWashReason] = useState("");
  /** Nhóm lý do (danh mục cố định) — chỉ dùng để thống kê ở báo cáo giặt lại. */
  const [reWashReasonCode, setReWashReasonCode] = useState<string>(REWASH_REASON_OPTIONS[0].value);
  const [reWashResponsible, setReWashResponsible] = useState<PickupStaff[]>([]);
  const [reWashSubmitting, setReWashSubmitting] = useState(false);
  const [reWashNeedPickup, setReWashNeedPickup] = useState(false); // false = phát hiện ngay
  const [reWashRequiresCounting, setReWashRequiresCounting] = useState(false);
  // Giảm giá (chỉ phát hiện sau): 0 = miễn phí, 1 = theo %, 2 = theo số tiền
  const [reWashDiscountType, setReWashDiscountType] = useState<0 | 1 | 2>(0);
  const [reWashDiscountValue, setReWashDiscountValue] = useState("");
  // Dịch vụ giữ lại cho đơn giặt lại — mặc định copy hết dòng đơn gốc, cho xoá bớt (giữ ≥ 1) + sửa SL.
  const [reWashRemovedLineIds, setReWashRemovedLineIds] = useState<number[]>([]);
  const [reWashLineQty, setReWashLineQty] = useState<Record<number, string>>({});
  useEffect(() => {
    if (!showReWash) {
      setReWashReason(""); setReWashResponsible([]);
      setReWashNeedPickup(false); setReWashRequiresCounting(false);
      setReWashDiscountType(0); setReWashDiscountValue("");
      setReWashRemovedLineIds([]); setReWashLineQty({});
    }
  }, [showReWash]);
  // Dòng dịch vụ của đơn gốc còn giữ lại + SL (chuỗi) hiện tại.
  const reWashServiceLines = useMemo(
    () =>
      items
        .filter((it) => Number(it.status) !== 8 && (Number(it.kind) !== 2 && Number(it.item_type) !== 2))
        .filter((it) => !reWashRemovedLineIds.includes(Number(it.id)))
        .map((it) => {
          const id = Number(it.id);
          const isKg =
            Number(it.measure_type) === 2 &&
            String(it.unit_name || "").trim().toLowerCase() === "kg";
          return {
            id,
            name: it.item_name ?? `#${it.item_id}`,
            unit: it.unit_name || "",
            price: Number(it.price) || 0,
            isKg,
            qty: reWashLineQty[id] ?? String(Number(it.quantity) || 0),
          };
        }),
    [items, reWashRemovedLineIds, reWashLineQty]
  );
  const setReWashLineQtyValue = (id: number, value: string, isKg: boolean) =>
    setReWashLineQty((prev) => ({
      ...prev,
      [id]: isKg ? value.replace(/[^0-9.,]/g, "").replace(",", ".") : value.replace(/[^0-9]/g, ""),
    }));

  // Equipment summary
  const [equipmentSummary, setEquipmentSummary] = useState<OrderEquipmentSummaryItem[]>([]);
  const [equipmentLoading, setEquipmentLoading] = useState(false);
  const { fetch: fetchEquipmentSummary } = EquipmentService.orderEquipmentSummary();

  // ===== Realtime cho tab "Quá trình & đối soát" =====
  // Tab dựng từ 6 nguồn (workflow log, điều chuyển, thiết bị, phiếu lỗi, vấn đề, tổng hợp thiết bị), tất cả
  // đều nạp 1 lần lúc mở modal. Bump `reloadKey` để nạp lại tất cả — mỗi effect bên dưới đã phụ thuộc key này.
  const [reloadKey, setReloadKey] = useState(0);
  // Gộp nhiều event sát nhau (quét túi → gắn thiết bị → đổi bước bắn liên tiếp) thành 1 lần nạp,
  // tránh 6 API × N event.
  const reloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleReload = useCallback(() => {
    if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
    reloadTimerRef.current = setTimeout(() => setReloadKey((k) => k + 1), 500);
  }, []);
  useEffect(() => () => { if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current); }, []);

  // Modal có thể mở ở ngữ cảnh chưa join room chi nhánh của đơn: sidebar đang chọn chi nhánh khác, hoặc
  // đơn đang điều chuyển nên chi nhánh chủ đơn ≠ chi nhánh giữ đồ.
  const orderBranchIds = useMemo(() => {
    const ids = [Number((order as any)?.branch_id), Number((order as any)?.current_branch_id)];
    return [...new Set(ids.filter((id) => id > 0))];
  }, [order]);

  // ⚠️ `leaveBranchRoom` KHÔNG đếm tham chiếu — rời room là rời hẳn cho cả tab. Nếu modal rời một room mà
  // trang danh sách đang cần thì trang đó mất realtime luôn. Nên chỉ tự quản lý những room mà trang KHÔNG
  // giữ:
  //   - Chưa chọn chi nhánh nào → trang đã join TẤT CẢ chi nhánh ⇒ modal không đụng vào.
  //   - Đã chọn chi nhánh X → trang chỉ giữ X ⇒ modal join/rời các chi nhánh khác X một cách an toàn.
  const { selectedBranch: ctxBranch } = useBranchContext();
  const roomsToManage = useMemo(
    () => (ctxBranch == null ? [] : orderBranchIds.filter((id) => id !== ctxBranch)),
    [ctxBranch, orderBranchIds],
  );
  useEffect(() => {
    if (!open || roomsToManage.length === 0 || !socketService.isConnected()) return;
    for (const bId of roomsToManage) socketService.joinBranchRoom(bId);
    return () => { for (const bId of roomsToManage) socketService.leaveBranchRoom(bId); };
  }, [open, roomsToManage]);

  // Chỉ quan tâm event của ĐÚNG đơn đang mở (event phát theo room chi nhánh nên nhận cả đơn khác).
  const handleOrderEvent = useCallback((data: OrderEventData) => {
    if (!open || !order?.id) return;
    if (Number((data as any)?.order_id) !== Number(order.id)) return;
    scheduleReload();
  }, [open, order?.id, scheduleReload]);

  useOrderRealtime(orderBranchIds, {
    onOrderStatusChanged: handleOrderEvent,   // gồm cả lưu kiểm đếm/phân loại + điều chuyển (BE emit from=to)
    onOrderAssigned: handleOrderEvent,
    onOrderStepAdvanced: handleOrderEvent,
    onOrderSplit: handleOrderEvent,
    onOrderCancelled: handleOrderEvent,
    onEquipmentAssigned: handleOrderEvent,
    onEquipmentReleased: handleOrderEvent,
  });

  // Chi tiết từng lượt gắn thiết bị (cả đang gắn lẫn đã trả) — cho bảng "Thiết bị sử dụng" ở tab Quá trình.
  const [equipmentDetail, setEquipmentDetail] = useState<any[]>([]);
  const { fetch: fetchEquipmentDetail } = EquipmentService.orderEquipmentDetail();
  const fetchEquipmentDetailRef = useRef(fetchEquipmentDetail);
  useEffect(() => { fetchEquipmentDetailRef.current = fetchEquipmentDetail; }, [fetchEquipmentDetail]);
  useEffect(() => {
    if (!open || !order?.id) { setEquipmentDetail([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchEquipmentDetailRef.current(order.id);
        if (!cancelled && res && isSuccess(res)) {
          const raw: any = res.data;
          setEquipmentDetail(Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : []);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [open, order?.id, reloadKey]);

  // Order Issues
  const [orderIssues, setOrderIssues] = useState<any[]>([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const { request: fetchIssuesApi } = useFetchData.Get<any>({
    projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
  });
  const fetchIssuesRef = useRef(fetchIssuesApi);
  useEffect(() => { fetchIssuesRef.current = fetchIssuesApi; }, [fetchIssuesApi]);

  // Workflow history (để lấy chi tiết pickup_check + inspect classification)
  const [workflowLogs, setWorkflowLogs] = useState<any[]>([]);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const { request: fetchWorkflowApi } = useFetchData.Get<any>({
    projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
  });
  const fetchWorkflowRef = useRef(fetchWorkflowApi);
  useEffect(() => { fetchWorkflowRef.current = fetchWorkflowApi; }, [fetchWorkflowApi]);

  useEffect(() => {
    if (!open || !order?.id) {
      setWorkflowLogs([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setWorkflowLoading(true);
      try {
        const res = await fetchWorkflowRef.current({ endPoint: API_ORDER_ROUTER.API_WORKFLOW_HISTORY(order.id) });
        if (!cancelled && res && isSuccess(res)) {
          // Backend trả { data: logs } → res.data có thể là array, { data: [] }, hoặc { list: [] }
          const raw: any = res.data;
          const list = Array.isArray(raw)
            ? raw
            : Array.isArray(raw?.data)
              ? raw.data
              : Array.isArray(raw?.list)
                ? raw.list
                : [];
          setWorkflowLogs(list);
        }
      } catch {
        // ignore
      } finally {
        if (!cancelled) setWorkflowLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, order?.id, reloadKey]);

  // Đơn đã/đang điều chuyển? (suy từ log workflow: TRANSFER_CREATE / TRANSFER_RECEIVE) → hiện pha "Điều chuyển".
  const hasTransfer = useMemo(() => {
    return (workflowLogs || []).some((l: any) => {
      let cd: any = l?.classification_data;
      if (typeof cd === "string") { try { cd = JSON.parse(cd); } catch { cd = null; } }
      return cd?.action === "TRANSFER_CREATE" || cd?.action === "TRANSFER_RECEIVE";
    });
  }, [workflowLogs]);

  // Đơn có đi XE TRUNG CHUYỂN không, và ở chiều nào? Suy từ log bàn giao xe (BE gắn
  // `classification_data.action = 'VEHICLE_HANDOVER'`). Log ghi `from_status` = trạng thái đơn lúc bàn giao
  // → 6/7 (Sẵn sàng giao / Đang giao) là chiều SẠCH, còn lại là chiều DƠ. Nhờ vậy thanh tiến trình chèn
  // chặng xe đúng chỗ nó thật sự cắt ngang quy trình.
  const vehicleLegs = useMemo(() => {
    const legs: { afterPickup?: boolean; afterTransfer?: boolean; afterReady?: boolean } = {};
    for (const l of workflowLogs || []) {
      let cd: any = (l as any)?.classification_data;
      if (typeof cd === "string") { try { cd = JSON.parse(cd); } catch { cd = null; } }
      if (cd?.action !== "VEHICLE_HANDOVER") continue;
      const st = Number((l as any).from_status);
      if (st === 6 || st === 7) legs.afterReady = true;        // hàng sạch rời nhà máy
      else if (st === 21) legs.afterTransfer = true;           // đơn điều chuyển: có phiếu rồi mới lên xe
      else legs.afterPickup = true;                            // đơn nhà máy: lấy hàng xong đưa lên xe
    }
    return legs;
  }, [workflowLogs]);

  // Phiếu điều chuyển của đơn — cho bảng "Điều chuyển & thời gian từng chặng" (Tab Quá trình).
  const [transfers, setTransfers] = useState<any[]>([]);
  const { request: fetchTransfersApi } = useFetchData.Get<any>({ projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE });
  const fetchTransfersRef = useRef(fetchTransfersApi);
  useEffect(() => { fetchTransfersRef.current = fetchTransfersApi; }, [fetchTransfersApi]);
  useEffect(() => {
    if (!open || !order?.id) { setTransfers([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchTransfersRef.current({ endPoint: API_ORDER_ROUTER.API_ORDER_TRANSFERS(order.id) });
        if (!cancelled && res && isSuccess(res)) {
          const raw: any = res.data;
          setTransfers(Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : []);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [open, order?.id, reloadKey]);

  // Loại A (điều chuyển tạo từ WAITING_ACCEPT — đơn chưa ai lấy hàng): phiếu điều chuyển KHÔNG nằm sau bước
  // "Lấy hàng"/"Kiểm tra" như Loại B, vì tại chi nhánh này đơn chưa từng được lấy/kiểm tra trước khi chuyển đi.
  // Dùng để đổi vị trí pha "Điều chuyển" trên thanh tiến trình cho đúng thực tế.
  const isEarlyTransfer = useMemo(
    () => (transfers || []).some((t: any) => Number(t.pre_transfer_status) === 1),
    [transfers]
  );

  // Phiếu phạt (fault tickets) của đơn — cho Tab "Quá trình & đối soát"
  const [faults, setFaults] = useState<any[]>([]);
  const [faultsLoading, setFaultsLoading] = useState(false);
  const [faultReloadKey, setFaultReloadKey] = useState(0);
  const [showFaultCreate, setShowFaultCreate] = useState(false);
  const { request: fetchFaultsApi } = useFetchData.Get<any>({ projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE });
  const fetchFaultsRef = useRef(fetchFaultsApi);
  useEffect(() => { fetchFaultsRef.current = fetchFaultsApi; }, [fetchFaultsApi]);

  useEffect(() => {
    if (!open || !order?.id) { setFaults([]); return; }
    let cancelled = false;
    (async () => {
      setFaultsLoading(true);
      try {
        const res = await fetchFaultsRef.current({ endPoint: `${API_FAULT_TICKET_ROUTER.API_LIST}?order_id=${order.id}&limit=100&page=1` });
        if (!cancelled && res && isSuccess(res)) {
          const raw: any = res.data;
          const list = Array.isArray(raw) ? raw : Array.isArray(raw?.list) ? raw.list : Array.isArray(raw?.data) ? raw.data : [];
          // Chỉ giữ phiếu chờ giải trình + đã chốt lỗi (liên quan đơn đang xem)
          const filtered = list.filter((f: any) => SHOWN_FAULT_STATUSES.includes(Number(f.status)));
          setFaults(filtered);
        }
      } catch {
        // ignore
      } finally {
        if (!cancelled) setFaultsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, order?.id, faultReloadKey, reloadKey]);

  // Tách logs theo mode
  const classifyGroups = useMemo(() => {
    // Giữ `category_id` (có ở log app mới) — cột "Nợ / trả" khớp với sổ nợ đồ theo ID trước, tên sau.
    const toItem = (i: any) => ({
      category: String(i.category || ''),
      quantity: Number(i.quantity) || 0,
      category_id: Number(i.category_id) > 0 ? Number(i.category_id) : null,
    });
    const parseCD = (cd: any) => {
      if (typeof cd === 'string') {
        try { return JSON.parse(cd); } catch { return null; }
      }
      return cd;
    };
    const byMode: Record<string, any> = {};
    // Mode PER-TÚI (khi nhận / đóng túi giao) → gom theo TỪNG TÚI (equipment_id): mỗi túi lấy log mới nhất.
    const perBagByMode: Record<string, Map<number, { code: string; items: any[]; defects: any[]; ts: number }>> = {};
    const wholeByMode: Record<string, { items: any[]; defects: any[]; ts: number }> = {};
    for (const log of workflowLogs) {
      const cd = parseCD(log.classification_data);
      if (!cd) continue;
      const ts = new Date(log.performed_at || log.created_at || 0).getTime();
      if (PER_BAG_MODES.includes(cd.mode)) {
        const m: string = cd.mode;
        const eid = Number(cd.equipment_id) || 0;
        const items = Array.isArray(cd.items) ? cd.items : [];
        const defects = Array.isArray(cd.defects) ? cd.defects : [];
        if (eid > 0) {
          if (!perBagByMode[m]) perBagByMode[m] = new Map();
          const ex = perBagByMode[m].get(eid);
          if (!ex || ts > ex.ts) perBagByMode[m].set(eid, { code: cd.equipment_code || String(eid), items, defects, ts });
        } else if (!wholeByMode[m] || ts > wholeByMode[m].ts) {
          wholeByMode[m] = { items, defects, ts };
        }
        continue;
      }
      if (!Array.isArray(cd.items)) continue;
      // Fallback 'inspect' CHỈ cho log cũ KHÔNG có `mode`. Mode lạ mà đổ vào 'inspect' thì log mới sẽ GHI ĐÈ
      // số của bước phân loại (đúng lỗi đã gặp với `production_done` trước khi khai báo mode này).
      if (cd.mode && !CLASSIFY_MODE_ORDER.includes(cd.mode)) continue;
      const mode: ClassifyMode = cd.mode || 'inspect';
      if (!byMode[mode] || ts > byMode[mode]._ts) {
        byMode[mode] = { ...log, _cd: cd, _ts: ts };
      }
    }

    // Breakdown theo túi cho từng mode per-túi
    const bagsByMode: Record<string, Array<{ code: string; items: { category: string; quantity: number; category_id?: number | null }[]; total: number; defects: any[] }>> = {};
    for (const m of PER_BAG_MODES) {
      const list: Array<{ code: string; items: { category: string; quantity: number; category_id?: number | null }[]; total: number; defects: any[] }> = [];
      const byBag = perBagByMode[m];
      if (byBag && byBag.size > 0) {
        for (const [, v] of byBag) {
          const items = v.items.map(toItem);
          list.push({ code: v.code, items, total: items.reduce((s, i) => s + i.quantity, 0), defects: v.defects });
        }
      } else if (wholeByMode[m]) {
        const items = wholeByMode[m].items.map(toItem);
        list.push({ code: '', items, total: items.reduce((s, i) => s + i.quantity, 0), defects: wholeByMode[m].defects });
      }
      bagsByMode[m] = list;
    }

    const result: Array<{
      mode: ClassifyMode;
      items: { category: string; quantity: number; category_id?: number | null }[];
      total: number;
      performedAt?: string;
      missingTotal?: number;
      missingNote?: string;
      confirmedMissing?: boolean;
      bags?: Array<{ code: string; items: { category: string; quantity: number; category_id?: number | null }[]; total: number; defects: any[] }>;
    }> = [];
    for (const mode of CLASSIFY_MODE_ORDER) {
      if (PER_BAG_MODES.includes(mode)) {
        const bags = bagsByMode[mode] || [];
        if (bags.length === 0) continue;
        const mergedMap = new Map<string, { category: string; quantity: number; category_id: number | null }>();
        for (const b of bags) {
          for (const it of b.items as any[]) {
            const cur = mergedMap.get(it.category);
            if (cur) {
              cur.quantity += it.quantity;
              if (!cur.category_id && it.category_id) cur.category_id = it.category_id;
            } else mergedMap.set(it.category, { ...it });
          }
        }
        const items = [...mergedMap.values()];
        result.push({ mode, items, total: items.reduce((s, i) => s + i.quantity, 0), bags });
        continue;
      }
      const entry = byMode[mode];
      if (!entry) continue;
      const items = (entry._cd.items || []).map(toItem);
      const total = entry._cd.total_items != null
        ? Number(entry._cd.total_items)
        : items.reduce((s: number, i: any) => s + i.quantity, 0);
      result.push({
        mode,
        items,
        total,
        performedAt: entry.performed_at || entry.created_at,
        missingTotal: entry._cd.missing_total != null ? Number(entry._cd.missing_total) : undefined,
        missingNote: entry._cd.missing_note || undefined,
        confirmedMissing: entry._cd.confirmed_missing === true,
      });
    }
    return result;
  }, [workflowLogs]);

  // Chênh lệch giữa 2 lần kiểm đếm
  const classifyDiff = useMemo(() => {
    const pickup = classifyGroups.find(g => g.mode === 'pickup_check');
    const inspect = classifyGroups.find(g => g.mode === 'inspect');
    if (!pickup || !inspect) return null;
    const diff = pickup.total - inspect.total;
    if (diff === 0) return null;
    return {
      diff,
      isMissing: diff > 0,
      note: inspect.missingNote,
      confirmed: inspect.confirmedMissing,
    };
  }, [classifyGroups]);

  useEffect(() => {
    if (!open || !order?.id) {
      setEquipmentSummary([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setEquipmentLoading(true);
      try {
        const res = await fetchEquipmentSummary(order.id);
        if (!cancelled && res?.status === 200) {
          const data = Array.isArray(res.data) ? res.data : [];
          setEquipmentSummary(data as OrderEquipmentSummaryItem[]);
        }
      } catch {
        // ignore
      } finally {
        if (!cancelled) setEquipmentLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, order?.id, reloadKey]);

  // Fetch order issues
  useEffect(() => {
    if (!open || !order?.id) {
      setOrderIssues([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setIssuesLoading(true);
      try {
        const res = await fetchIssuesRef.current({ endPoint: API_ORDER_ROUTER.API_ISSUES(order.id) });
        if (!cancelled && res && isSuccess(res)) {
          const list = res.data?.list ?? res.data ?? [];
          setOrderIssues(Array.isArray(list) ? list : []);
        }
      } catch {
        // ignore
      } finally {
        if (!cancelled) setIssuesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, order?.id, reloadKey]);

  // In phiếu giao nhận: mount ReconcilePrintSheet (portal, chỉ hiện khi in) rồi gọi window.print().
  const [showPrint, setShowPrint] = useState(false);
  useEffect(() => {
    if (!showPrint) return;
    const done = () => setShowPrint(false);
    window.addEventListener("afterprint", done);
    const t = setTimeout(() => window.print(), 100);
    return () => { clearTimeout(t); window.removeEventListener("afterprint", done); };
  }, [showPrint]);

  // ✅ Sau đó mới kiểm tra open và order
  if (!open || !order) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-[1200px] max-w-[97vw] max-h-[88vh] overflow-y-auto p-6 shadow-2xl border border-slate-200">
        <div className="flex items-center justify-between border-b pb-3 mb-4">
          <h3 className="text-lg font-semibold text-slate-800">
            Chi tiết đơn hàng
          </h3>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 text-lg"
          >
            ✕
          </button>
        </div>

        {/* Sub-order indicator - click to go back to parent */}
        {order.parent_order_id && (
          <div
            className={`bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2 mb-4 text-sm text-indigo-700 flex items-center gap-2 ${onViewParentOrder ? 'cursor-pointer hover:bg-indigo-100 transition' : ''}`}
            onClick={() => onViewParentOrder?.(order.parent_order_id!)}
          >
            {onViewParentOrder && (
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            )}
            <span>
              Đơn con #{order.sub_order_index} của đơn gốc
              {order.code && (
                <span className="ml-2 px-2 py-0.5 bg-indigo-100 rounded-full text-xs font-semibold">
                  {String(order.code).split('-')[0]}
                </span>
              )}
              {/* {order.item_count != null && (
                <span className="ml-1 text-xs">({order.item_count} cái)</span>
              )} */}
            </span>
          </div>
        )}


        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200 mb-4">
          <button
            onClick={() => setActiveTab("info")}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${activeTab === "info" ? "border-green-500 text-green-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          >Thông tin</button>
          <button
            onClick={() => setActiveTab("process")}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${activeTab === "process" ? "border-green-500 text-green-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          >Quá trình &amp; đối soát</button>
        </div>

        {activeTab === "info" && (
        <>
        {/* Sổ nợ đồ + dự kiến thiếu (chỉ đơn kiểm đếm — BE chỉ trả dữ liệu cho loại đơn này).
            Giám sát nhìn được ngay khách đang nợ gì để nhắc chi nhánh trả bù, không phải chờ cuối kỳ. */}
        {(!!(order as any).expected_shortage?.total || !!(order as any).customer_item_debt?.length) && (
          <div className="mb-4 space-y-2">
            {!!(order as any).expected_shortage?.total && (
              <div className="bg-rose-50 border border-rose-200 rounded-lg px-4 py-2 text-sm text-rose-800">
                <b>Dự kiến thiếu {(order as any).expected_shortage.total} cái khi giao:</b>{" "}
                {(order as any).expected_shortage.items
                  .map((i: any) => `${i.category} ${i.quantity}`)
                  .join(", ")}
              </div>
            )}
            {!!(order as any).customer_item_debt?.length && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm text-amber-800">
                <b>Khách đang bị nợ đồ:</b>{" "}
                {(order as any).customer_item_debt
                  .map((i: any) => `${i.category} ${i.quantity}`)
                  .join(", ")}
                <span className="text-amber-600"> — giao bù ở đơn sau sẽ tự trừ nợ khi kiểm đếm giao.</span>
              </div>
            )}
          </div>
        )}

        {/* Truy vết nợ – trả CỦA RIÊNG ĐƠN NÀY (khác 2 khối trên — số dư/cảnh báo theo khách):
            trả lời trực tiếp "nợ của đơn này được trả ở đơn nào" / "đơn này trả bù cho đơn nào".
            Rỗng danh sách `settlements`/`settles` không có nghĩa chưa trả — có thể do ghi TRƯỚC khi
            tính năng này tồn tại (không backfill dữ liệu cũ, xem migration `create-customer-item-debt-matches.sql`). */}
        {(!!(order as any).item_debt_trace?.debts_created?.length ||
          !!(order as any).item_debt_trace?.repayments_made?.length) && (
          <div className="mb-4 space-y-2">
            {(order as any).item_debt_trace.debts_created.map((d: any, idx: number) => (
              <div
                key={`owe-${idx}`}
                className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-sm text-blue-800"
              >
                <b>Đơn này ghi nợ {d.quantity} {d.category}</b>
                {d.settlements.map((s: any, i: number) => (
                  <div key={i}>
                    ✓ Đã trả bù {s.quantity} cái ở đơn {s.order_code || (s.order_id ? `#${s.order_id}` : "không rõ")}
                  </div>
                ))}
                {d.remaining > 0 && (
                  <div className="text-blue-600 italic">Còn treo {d.remaining} cái chưa trả bù.</div>
                )}
              </div>
            ))}
            {(order as any).item_debt_trace.repayments_made.map((r: any, idx: number) => (
              <div
                key={`return-${idx}`}
                className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-sm text-blue-800"
              >
                <b>Đơn này trả bù {r.quantity} {r.category}</b>
                {r.settles.map((s: any, i: number) => (
                  <div key={i}>
                    Cho khoản nợ phát sinh ở đơn {s.order_code || (s.order_id ? `#${s.order_id}` : "không rõ")} ({s.quantity} cái)
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Đang nằm trên xe trung chuyển — trả lời "hàng đang ở đâu" khi đồ không nằm ở chi nhánh nào
            (điểm trung chuyển không phải chi nhánh, nên `current_branch_name` không đủ). */}
        {!!(order as any).vehicle_trip && (
          <div className="mb-4 bg-orange-50 border border-orange-200 rounded-lg px-4 py-2 text-sm text-orange-800">
            <b>🚚 Đang trên xe trung chuyển:</b>{" "}
            {(order as any).vehicle_trip.vehicle_name || "Xe"} ·{" "}
            {(order as any).vehicle_trip.plate_number || "—"}
            {Number((order as any).vehicle_trip.direction) === 1
              ? " — chở hàng dơ về nhà máy"
              : " — chở hàng sạch ra điểm trung chuyển"}
            {(order as any).vehicle_trip.boarded_at && (
              <span className="text-orange-600">
                {" "}(lên xe {formatServerDateTime((order as any).vehicle_trip.boarded_at)})
              </span>
            )}
          </div>
        )}


        {/* Đặc trưng đơn GIẶT LẠI */}
        {Number(order.order_type) === 2 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-0.5 bg-orange-600 text-white text-xs font-semibold rounded-full">Đơn giặt lại</span>
              <span className="text-sm text-orange-800 font-medium">
                {Number(order.is_pickup_laundry) === 1 ? "Khách trả sau — cần nhận lại đồ" : "Khách trả ngay — đồ đã ở cửa hàng"}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm text-slate-700">
              <div>
                <b>Đơn gốc liên quan:</b>{" "}
                {order.related_order_code || (order.related_order_id ? `#${order.related_order_id}` : "Không xác định")}
              </div>
              <div>
                <b>NV chịu trách nhiệm:</b>{" "}
                {order.responsible_names || (order.responsible_user_ids ? `NV #${order.responsible_user_ids.split(",").join(", #")}` : "—")}
              </div>
              <div>
                <b>Cách thu tiền:</b>{" "}
                {/* Tách lúc trả hàng (mode "trừ số lượng"): giá đã CHỐT ở đơn này và đã bị trừ khỏi đơn gốc
                    → cân lại sau khi giặt KHÔNG đổi số phải thu. */}
                {order.rewash_locked_amount != null
                  ? `Đã chốt ${formatCurrency(order.rewash_locked_amount)}đ (đã trừ ở đơn gốc)`
                  : Number(order.rewash_discount_type) === 1
                  ? `Giảm ${Number(order.rewash_discount_value) || 0}% tổng bill`
                  : Number(order.rewash_discount_type) === 2
                  ? `Giảm ${formatCurrency(order.rewash_discount_value || 0)}`
                  : Number(order.rewash_discount_type) === 3
                  ? "Thu đủ theo số ký"
                  : "Miễn phí (0đ)"}
              </div>
              <div className="col-span-2">
                <b>Lý do giặt lại:</b>{" "}
                {(order.note || "").replace(/^\s*\[Giặt lại\]\s*/, "").trim() || "—"}
              </div>
            </div>
          </div>
        )}

        {/* Đơn CHÍNH đã tách đồ sang đơn giặt lại lúc trả hàng → giải thích vì sao số cần thu nhỏ hơn
            (final_weight × đơn giá). Không có dòng này người xem sẽ tưởng đơn bị tính sai tiền. */}
        {Number(order.order_type) !== 2 && Number(order.rewash_deduct_amount) > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-orange-800">Đã trừ tiền giặt lại</span>
              <span className="text-sm font-semibold text-orange-700">
                −{formatCurrency(order.rewash_deduct_amount)}đ
              </span>
            </div>
            <p className="text-xs text-orange-600 mt-1">
              Phần đồ chưa đạt đã tách sang đơn giặt lại
              {order.open_rewash_order?.code ? ` (${order.open_rewash_order.code})` : ""}, khách chỉ trả phần đồ sạch.
            </p>
          </div>
        )}

        {/* Thông tin đơn */}
        <div className="grid grid-cols-2 gap-3 text-sm mb-6">
          <div><b>Mã phiếu:</b> {order.code ?? `OD-${order.id}`}</div>
          <div><b>Khách hàng:</b> {order.customer?.name ?? "—"}</div>
          <div><b>Chi nhánh:</b> {order.branch?.name ?? "—"}</div>
          <div>
            <b>NV Sale:</b>{" "}
            {order.employeeSale?.name ? (
              <span className="font-medium text-slate-800">
                {order.employeeSale.name}
                {order.employeeSale.phone ? ` (${order.employeeSale.phone})` : ""}
              </span>
            ) : (
              <span className="text-slate-400">—</span>
            )}
          </div>
          <div>
            <b>Trạng thái:</b>{" "}
            <span
              className={`px-2 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap min-w-[120px] inline-block text-center ${
                STATUS_BADGE_STYLES[Number(order.status)] || "bg-gray-100 text-gray-500"
              }`}
            >
              {ORDER_STATUS_LABELS[Number(order.status)] || "Không rõ"}
            </span>
          </div>
          <div>
            <b>Công nợ:</b>{" "}
            {Number(order.is_debit) === IS_DEBIT.NO_DEBIT
              ? "Thanh toán liền"
              : Number(order.is_debit) === IS_DEBIT.DEBIT
              ? "Nợ"
              : "Không xác định"}
          </div>
          <div>
            <b>Hình thức thanh toán:</b>{" "}
            {Number(order.payment_method) === PAYMENT_METHOD.CASH
              ? "Tiền mặt"
              : Number(order.payment_method) === PAYMENT_METHOD.QR_CODE
              ? "Chuyển khoản"
              : Number(order.payment_method) === PAYMENT_METHOD.CONG_NO
              ? "Công nợ"
              : "Không xác định"}
            {canChangePaymentMethod && (
              <button
                type="button"
                onClick={() => setShowChangePayment(true)}
                title="Đổi hình thức thanh toán"
                className="ml-2 inline-flex items-center gap-1 rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 align-middle text-xs font-medium text-teal-700 transition hover:border-teal-300 hover:bg-teal-100"
              >
                <ArrowLeftRight className="h-3 w-3" />
                Đổi HTTT
              </button>
            )}
            {canCancelCashDeposit && (
              <button
                type="button"
                onClick={() => setShowCancelCashDeposit(true)}
                title="Huỷ khoản thu + lần nộp tiền mặt đã nộp sổ quỹ (để đổi được hình thức thanh toán)"
                className="ml-2 inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 align-middle text-xs font-medium text-red-700 transition hover:border-red-300 hover:bg-red-100"
              >
                <Undo2 className="h-3 w-3" />
                Hủy SQTM
              </button>
            )}
          </div>
          <div className="col-span-2"><b>Địa chỉ:</b> {order.address ?? "—"}</div>
          <div className="col-span-2">
            <b>Ghi chú:</b>{" "}
            {editingNote ? (
              <span className="inline-flex items-start gap-2 w-full mt-1">
                <textarea
                  value={noteValue}
                  onChange={(e) => setNoteValue(e.target.value)}
                  className="flex-1 border rounded px-2 py-1 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                  rows={2}
                  placeholder="Nhập ghi chú..."
                  autoFocus
                />
                <button
                  disabled={savingNote}
                  onClick={async () => {
                    if (!onUpdateNote || !order.id) return;
                    setSavingNote(true);
                    try {
                      await onUpdateNote(order.id, noteValue);
                      setEditingNote(false);
                    } finally {
                      setSavingNote(false);
                    }
                  }}
                  className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {savingNote ? "..." : "Lưu"}
                </button>
                <button
                  onClick={() => setEditingNote(false)}
                  className="px-2 py-1 border text-xs rounded hover:bg-slate-50"
                >
                  Hủy
                </button>
              </span>
            ) : (
              <span>
                {order.note || "—"}
                {onUpdateNote && (
                  <button
                    onClick={() => { setNoteValue(order.note || ""); setEditingNote(true); }}
                    className="ml-2 text-blue-600 hover:text-blue-800 text-xs underline"
                  >
                    Sửa
                  </button>
                )}
              </span>
            )}
          </div>
          <div><b>Tạo lúc:</b> {formatServerDateTime(order.created_at)}</div>
          <div><b>Người tạo:</b> {order.employeeCreate?.name ?? "—"}</div>
          <div><b>Cập nhật:</b> {formatServerDateTime(order.updated_at)}</div>
          <div><b>Hẹn giờ giao:</b> {order.scheduled_delivery_at
                      ? (() => {
                          const d = new Date(order.scheduled_delivery_at);
                          const dd = d.getDate().toString().padStart(2, "0");
                          const MM = (d.getMonth() + 1).toString().padStart(2, "0");
                          const yyyy = d.getFullYear();
                          const hh = d.getHours().toString().padStart(2, "0");
                          const mm = d.getMinutes().toString().padStart(2, "0");
                          return `${dd}/${MM}/${yyyy} ${hh}:${mm}`;
                        })()
                      : "—"}</div>
          {/* Vận chuyển THẲNG bằng xe tải theo chiều: biển số + tài xế + phụ xe (đơn trung chuyển không hiện).
              Xem user/docs/xe-tai-doi-hinh-ky-thuat.md. */}
          {[
            { label: "Xe tải lấy hàng", leg: (order as any).pickup_vehicle },
            { label: "Xe tải giao hàng", leg: (order as any).delivery_vehicle },
          ]
            .filter((x) => !!x.leg)
            .map(({ label, leg }) => {
              const crew: any[] = leg.crew || [];
              const driver = crew.find((c) => c.position === "driver") || crew[0];
              const assistant = crew.find((c) => c !== driver);
              const person = (c: any) =>
                c ? `${c.employee_name || `NV #${c.employee_id}`}${c.weight_ratio ? ` (${Number(c.weight_ratio)}%)` : ""}` : "—";
              return (
                <div key={label}>
                  <b>{label}:</b> {leg.plate_number}
                  {leg.name ? <span className="text-slate-500"> ({leg.name})</span> : null}
                  <div className="text-slate-600">
                    Tài xế: {person(driver)}
                    {assistant && <> · Phụ xe: {person(assistant)}</>}
                  </div>
                </div>
              );
            })}
        </div>

        {/* Thanh quy trình — tiến độ đơn hàng */}
        <div className="rounded-lg border border-slate-200 px-4 py-4 mb-6">
          <OrderProgressBar
            status={Number(order.status)}
            requiresCounting={Number((order as any).requires_counting)}
            hasTransfer={hasTransfer}
            hasSplit={Number(order.has_sub_orders) === 1}
            isEarlyTransfer={isEarlyTransfer}
            vehicleLegs={vehicleLegs}
          />
        </div>

        {/* Weight Info - New Section */}
        <div className="bg-blue-50 rounded-lg p-4 mb-4">
          <h4 className="text-sm font-semibold text-blue-800 mb-3">Thông tin cân</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="bg-white rounded-lg p-3 border border-blue-100">
              <div className="text-xs text-slate-500 mb-1">Số ký khi nhận (cân lần 1)</div>
              <div className="text-lg font-semibold text-blue-600">
                {Number(order.pickup_weight) > 0 ? `${formatKg(order.pickup_weight)} kg` : "—"}
              </div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-blue-100">
              <div className="flex items-start justify-between gap-2">
                <div className="text-xs text-slate-500 mb-1">Sản lượng sau giặt (cân lần 2)</div>
                {canEditFinalWeight && (
                  <button
                    type="button"
                    onClick={() => setShowEditFinalWeight(true)}
                    className="inline-flex shrink-0 items-center gap-1 rounded-md border border-blue-200 px-2 py-0.5 text-xs font-medium text-blue-600 transition hover:bg-blue-50"
                  >
                    <Pencil className="h-3 w-3" /> Sửa
                  </button>
                )}
              </div>
              <div className="text-lg font-semibold text-slate-600">
                {Number(order.final_weight) > 0 ? `${formatKg(order.final_weight)} kg` : "—"}
              </div>
              {/* <div className="text-[11px] text-slate-400 mt-1">
                Chỉ số năng suất xưởng — không phải căn cứ tính tiền
              </div> */}
            </div>
          </div>
        </div>

        {/* Chi tiết sản phẩm/dịch vụ & thành tiền */}
        {(() => {
          // Cột cố định: STT, Tên, Loại, ĐVT, SL đăng ký, SL thực tế, Đơn giá, Thành tiền
          const colCount = 8;
          const labelSpan = colCount - 2; // chừa 2 cột cuối cho số tiền
          // Đơn đã HOÀN TẤT: ẩn dòng thành tiền = 0 (dòng đăng ký nhưng thực tế không phát sinh) cho bảng gọn.
          // Chỉ ẩn ở bảng hiển thị — tổng tiền không đổi, và form "Chỉnh SL / đơn giá" vẫn có đủ dòng.
          const lineAmount = (it: any) => Number(it.total_price) || Number(it.price) * (Number(it.quantity) || 0);
          const visibleItems =
            Number(order.status) === ORDER_STATUS.COMPLETE ? items.filter((it) => lineAmount(it) !== 0) : items;
          return (
        <div className="border rounded-lg overflow-hidden shadow-sm mb-4">
          {/* Cách tính tiền — nêu rõ 2 thời điểm chốt số (yêu cầu của thiết kế: mọi màn hiện tiền phải nói rõ) */}
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-xs text-amber-800 flex items-start justify-between gap-3">
            <span>
              Tiền hàng = <b>Σ (SL thực tế × đơn giá)</b> trên từng dòng. Dòng theo <b>cái</b> chốt ở biên bản
              kiểm đếm; dòng theo <b>kg</b> chốt khi giao khách.
            </span>
            {canEditItems && items.length > 0 && (
              <button
                type="button"
                onClick={() => setShowEditItems(true)}
                className="shrink-0 px-3 py-1 rounded-md bg-white border border-amber-300 text-amber-700 font-medium hover:bg-amber-100 transition"
              >
                Chỉnh SL / đơn giá
              </button>
            )}
          </div>
          <table className="w-full text-sm text-slate-700">
            <thead className="bg-slate-100 text-slate-700 uppercase text-xs">
              <tr>
                <th className="px-4 py-3 text-center w-[60px]">STT</th>
                <th className="px-4 py-3 text-left">Tên sản phẩm / Dịch vụ</th>
                <th className="px-4 py-3 text-left w-[120px]">Loại</th>
                <th className="px-4 py-3 text-center w-[70px]">ĐVT</th>
                <th className="px-4 py-3 text-center w-[100px]">SL đăng ký</th>
                <th className="px-4 py-3 text-center w-[90px]">SL thực tế</th>
                <th className="px-4 py-3 text-right w-[120px]">Đơn giá</th>
                <th className="px-4 py-3 text-right w-[140px]">Thành tiền</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {visibleItems.length > 0 ? (
                visibleItems.map((it, idx) => {
                  const isSurcharge = Number(it.kind) === 2 || Number(it.item_type) === 2;
                  const qty = Number(it.quantity) || 0;
                  const qtyReg = Number(it.quantity_registered) || 0;
                  return (
                  <tr key={it.id ?? idx} className={`${idx % 2 === 0 ? "bg-white" : "bg-slate-50"} hover:bg-slate-100 transition`}>
                    <td className="px-4 py-2 text-center text-slate-700 font-medium">{idx + 1}</td>
                    <td className="px-4 py-2 font-medium text-slate-800">{it.item_name ?? `#${it.item_id}`}</td>
                    <td className="px-4 py-2">
                      {isSurcharge ? (
                        <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">Phụ phí</span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">Dịch vụ</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center text-slate-500">{it.unit_name || "—"}</td>
                    <td className="px-4 py-2 text-center text-slate-400">
                      {qtyReg > 0 ? qtyReg : "—"}
                    </td>
                    <td className="px-4 py-2 text-center font-medium">{qty}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(it.price)}</td>
                    <td className="px-4 py-2 text-right font-semibold text-slate-800">
                      {formatCurrency(lineAmount(it))}
                    </td>
                  </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={colCount} className="px-4 py-6 text-center italic text-slate-500 bg-slate-50">
                    {items.length > 0 ? "Không có dòng nào phát sinh tiền" : "Không có sản phẩm trong đơn hàng này"}
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot className="bg-slate-50 text-slate-800">
              <tr className="font-semibold border-t border-slate-200">
                <td colSpan={labelSpan} className="px-4 py-2 text-right">
                  Tổng tiền hàng{commonUnit ? ` (${totalQuantity} ${commonUnit})` : ""}:
                </td>
                <td colSpan={2} className="px-4 py-2 text-right">{formatCurrency(lineGross)}</td>
              </tr>
              <tr className="text-orange-600">
                <td colSpan={labelSpan} className="px-4 py-2 text-right">
                  Giảm giá
                  {/* Đơn giặt lại giảm theo % → nêu rõ % bên cạnh số tiền đã quy đổi. */}
                  {Number((order as any).rewash_discount_type) === 1 && Number((order as any).rewash_discount_value) > 0
                    ? ` (${Number((order as any).rewash_discount_value)}% tổng tiền hàng)`
                    : ""}:
                </td>
                <td colSpan={2} className="px-4 py-2 text-right">{Number(order.amount_discount) > 0 ? `-${formatCurrency(order.amount_discount)}` : formatCurrency(0)}</td>
              </tr>
              {Number(order.rewash_deduct_amount) > 0 && (
                <tr className="text-orange-600">
                  <td colSpan={labelSpan} className="px-4 py-2 text-right">Đã trừ tiền giặt lại (đồ tách sang đơn giặt lại):</td>
                  <td colSpan={2} className="px-4 py-2 text-right">-{formatCurrency(order.rewash_deduct_amount)}</td>
                </tr>
              )}
              <tr className="font-bold text-green-700 border-t-2 border-slate-300 bg-slate-100">
                <td colSpan={labelSpan} className="px-4 py-2 text-right">Thành tiền (cần thu):</td>
                <td colSpan={2} className="px-4 py-2 text-right">{formatCurrency(order.total_amount)}</td>
              </tr>
              <tr className="text-slate-700">
                <td colSpan={labelSpan} className="px-4 py-2 text-right">Hình thức thu tiền:</td>
                <td colSpan={2} className="px-4 py-2 text-right font-medium">
                  {Number(order.is_debit) === IS_DEBIT.DEBIT ? "Ghi nợ (công nợ)"
                    : Number(order.payment_method) === PAYMENT_METHOD.CASH ? "Tiền mặt"
                    : Number(order.payment_method) === PAYMENT_METHOD.QR_CODE ? "Chuyển khoản" : "—"}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
          );
        })()}

        {/* Hình ảnh xác nhận đơn hàng */}
        {(order.caption_images || order.image_document || editablePhotoSteps.length > 0) && (
          <div className="bg-purple-50 rounded-lg p-4 mb-4 border border-purple-100">
            <h4 className="text-sm font-semibold text-purple-800 mb-3 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Hình ảnh xác nhận
            </h4>
            {/* Sửa ảnh theo bước — quyền ORDER_PHOTO_EDIT */}
            {editablePhotoSteps.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {editablePhotoSteps.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setEditingPhotoKey(s.key)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-purple-300 bg-white text-xs font-medium text-purple-700 hover:bg-purple-100 transition"
                  >
                    {s.urls.length > 0 ? <Pencil className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                    {s.urls.length > 0 ? `Sửa ${s.label.toLowerCase()}` : `Thêm ${s.label.toLowerCase()}`}
                  </button>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {/* Parse và hiển thị caption_images */}
              {order.caption_images && (() => {
                try {
                  const captions = typeof order.caption_images === 'string'
                    ? JSON.parse(order.caption_images)
                    : order.caption_images;

                  // Key = trạng thái NƠI CHỤP (BE `mergeCaptions`). Chỉ còn các key BE đang ghi:
                  //  2 lấy hàng · 9 kiểm đếm nhận · 4 hoàn thành giặt (luồng đơn giản, không bắt buộc)
                  //  · 18 kiểm đếm giao · 5 = COMPLETE: ảnh lúc bấm "Hoàn thành" giao hàng.
                  const STATUS_LABELS: Record<string, string> = {
                    '2': 'Ảnh lấy hàng',
                    '9': 'Ảnh kiểm đếm (khi nhận)',
                    '4': 'Ảnh hoàn thành giặt',
                    '18': 'Ảnh kiểm đếm giao',
                    '5': 'Ảnh giao hàng',
                    // Key luồng cũ — BE không ghi nữa, giữ để ảnh của đơn cũ vẫn có nhãn đúng.
                    '3': 'Ảnh chờ giặt',
                    '6': 'Ảnh sẵn sàng giao',
                    '7': 'Ảnh giao hàng',
                    '10': 'Ảnh kiểm tra',
                    '11': 'Ảnh giặt (CT)',
                    '12': 'Ảnh sấy',
                    '13': 'Ảnh ủi',
                    '14': 'Ảnh gấp',
                  };

                  // caption_images[status] = 1 chuỗi HOẶC mảng chuỗi (bước kiểm đếm cho nhiều ảnh).
                  return Object.entries(captions).flatMap(([status, val]) => {
                    const urls: string[] = Array.isArray(val)
                      ? (val as string[]).filter(Boolean)
                      : val ? [val as string] : [];
                    return urls.map((url, i) => (
                      <div key={`${status}-${i}`} className="relative group">
                        <ImageCustom
                          url={url}
                          isLocal={false}
                          isUser={false}
                          isReview={true}
                          className="w-full h-32 object-cover rounded-lg border border-purple-200 cursor-pointer hover:opacity-90 transition"
                        />
                        <span className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                          {STATUS_LABELS[status] || `Trạng thái ${status}`}
                          {urls.length > 1 ? ` ${i + 1}/${urls.length}` : ''}
                        </span>
                      </div>
                    ));
                  });
                } catch {
                  return null;
                }
              })()}

              {/* Chứng từ thanh toán nợ */}
              {order.image_document && (
                <div className="relative group">
                  <ImageCustom
                    url={order.image_document}
                    isLocal={false}
                    isUser={false}
                    isReview={true}
                    className="w-full h-32 object-cover rounded-lg border border-purple-200 cursor-pointer hover:opacity-90 transition"
                  />
                  <span className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                    Chứng từ nợ
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Employee Assignments - New Section */}
        {order.employees && order.employees.length > 0 && (
          <div className="bg-green-50 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-green-800">Nhân viên được phân công</h4>
              {onEditWeights && (
                <button
                  onClick={onEditWeights}
                  className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                >
                  Nhập số ký
                </button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-green-700 bg-green-100/50">
                  <tr>
                    <th className="px-3 py-2 text-left">Nhân viên</th>
                    <th className="px-3 py-2 text-center">Công việc</th>
                    <th className="px-3 py-2 text-center">Số cái</th>
                    <th className="px-3 py-2 text-center">Số ký</th>
                    <th className="px-3 py-2 text-center">Trạng thái</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-green-200">
                  {/* Sắp theo TRÌNH TỰ công việc của đơn (không theo thứ tự ghi order_employees):
                      kiểm đếm nhận → lấy hàng → trung chuyển dơ → kiểm tra → giặt/sấy/ủi/gấp → trung chuyển sạch
                      → giao hàng → kiểm đếm giao. Cùng vai giữ thứ tự gốc (sort ổn định). */}
                  {[...order.employees]
                    .sort((a, b) => {
                      const STEP_ORDER = [9, 1, 10, 4, 2, 5, 6, 7, 8, 11, 3, 12];
                      const rank = (r: number) => {
                        const i = STEP_ORDER.indexOf(Number(r));
                        return i === -1 ? STEP_ORDER.length : i;
                      };
                      return rank(a.role_type) - rank(b.role_type);
                    })
                    .map((emp) => {
                    const roleLabels: Record<number, string> = {
                      1: "Lấy hàng",
                      2: "Giặt",
                      3: "Giao hàng",
                      4: "Kiểm tra",
                      5: "Giặt",
                      6: "Sấy",
                      7: "Ủi",
                      8: "Gấp",
                      9: "Kiểm đếm",
                      10: "Trung chuyển hàng dơ",  // xe trung chuyển: chở hàng dơ về nhà máy
                      11: "Trung chuyển hàng sạch", // xe trung chuyển: chở hàng sạch ra điểm tập kết
                      12: "Kiểm đếm giao",          // kiểm đếm với khách khi giao hàng (DELIVERY_COUNTER)
                    };
                    const roleColorMap: Record<number, string> = {
                      1: "bg-blue-100 text-blue-700",
                      2: "bg-purple-100 text-purple-700",
                      3: "bg-green-100 text-green-700",
                      4: "bg-cyan-100 text-cyan-700",
                      5: "bg-indigo-100 text-indigo-700",
                      6: "bg-orange-100 text-orange-700",
                      7: "bg-rose-100 text-rose-700",
                      8: "bg-teal-100 text-teal-700",
                      9: "bg-violet-100 text-violet-700",
                      10: "bg-amber-100 text-amber-700",
                      11: "bg-amber-100 text-amber-700",
                      12: "bg-violet-100 text-violet-700",
                    };
                    const statusLabels: Record<number, { text: string; class: string }> = {
                      1: { text: "Đã phân công", class: "bg-yellow-100 text-yellow-700" },
                      2: { text: "Đã nhận", class: "bg-blue-100 text-blue-700" },
                      3: { text: "Từ chối", class: "bg-red-100 text-red-700" },
                      4: { text: "Hoàn thành", class: "bg-green-100 text-green-700" },
                    };
                    const status = statusLabels[emp.status] || { text: "Không rõ", class: "bg-gray-100 text-gray-700" };
                    return (
                      <tr key={emp.id} className="bg-white/50 hover:bg-white transition">
                        <td className="px-3 py-2 font-medium text-slate-800">
                          {emp.employee?.name ?? `NV #${emp.employee_id}`}
                          {(emp as any).branch_id && Number((emp as any).branch_id) !== Number(order.branch_id) && (
                            <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-orange-50 text-orange-700 whitespace-nowrap">
                               {(emp as any).branch_name || `CN #${(emp as any).branch_id}`}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                            roleColorMap[emp.role_type] || "bg-gray-100 text-gray-700"
                          }`}>
                            {roleLabels[emp.role_type] || "Không rõ"}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center font-medium">
                          {Number(emp.item_count) > 0 ? emp.item_count : "—"}
                        </td>
                        <td className="px-3 py-2 text-center font-medium">
                          {Number(emp.assigned_weight) > 0 ? `${formatKg(emp.assigned_weight)} kg` : "—"}
                        </td>
                        {/* <td className="px-3 py-2 text-right">
                          {emp.wage_rate ? formatCurrency(emp.wage_rate) : "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-green-600">
                          {emp.calculated_wage ? formatCurrency(emp.calculated_wage) : "—"}
                        </td> */}
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${status.class}`}>
                            {status.text}
                          </span>
                          {emp.status === 3 && emp.reject_reason && (
                            <div className="text-xs text-red-500 mt-1">{emp.reject_reason}</div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Classification Section */}
        {order.classification_category && (
          <div className="bg-indigo-50 rounded-lg p-4 mb-4 border border-indigo-200">
            <h4 className="text-sm font-semibold text-indigo-800 mb-2 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              Phân loại
            </h4>
            <span className="inline-flex px-3 py-1 bg-indigo-100 text-indigo-700 text-sm font-medium rounded-full">
              {order.classification_category}
            </span>
            {order.item_count != null && order.item_count > 0 && (
              <span className="ml-2 text-sm text-indigo-600">{order.item_count} cái</span>
            )}
          </div>
        )}

        {/* Classification Details (Phase 3) - chi tiết pickup_check & inspect */}
        {/* {(classifyGroups.length > 0 || workflowLoading) && (
          <div className="bg-slate-50 rounded-lg p-4 mb-4 border border-slate-200">
            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              Kiểm đếm & Phân loại
            </h4>
            {workflowLoading ? (
              <p className="text-xs text-slate-500">Đang tải...</p>
            ) : (
              <div className="space-y-3">
                {classifyGroups.map((g, gi) => (
                  <div key={gi} className="bg-white rounded-md border border-slate-200 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${CLASSIFY_MODE_META[g.mode].cls}`}
                      >
                        {CLASSIFY_MODE_META[g.mode].label}
                      </span>
                      <span className="text-xs text-slate-500">
                        Tổng {g.total} cái
                        {g.performedAt && (
                          <span className="ml-2 text-slate-400">
                            · {new Date(g.performedAt).toLocaleString('vi-VN')}
                          </span>
                        )}
                      </span>
                    </div>
                    {g.items.length === 0 ? (
                      <p className="text-xs text-slate-400 italic">Không có loại đồ</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {g.items.map((it, ii) => (
                          <div key={ii} className="flex justify-between text-sm py-1 px-2 bg-slate-50 rounded">
                            <span className="text-slate-700">{it.category || '—'}</span>
                            <span className="font-semibold text-slate-900">×{it.quantity}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {classifyDiff && (
                  <div className="bg-red-50 rounded-md border-l-4 border-red-500 border border-red-200 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span className="text-sm font-bold text-red-800">
                        {classifyDiff.isMissing
                          ? `Thiếu ${classifyDiff.diff} cái so với khi nhận`
                          : `Dư ${Math.abs(classifyDiff.diff)} cái so với khi nhận`}
                      </span>
                      {classifyDiff.confirmed && (
                        <span className="ml-auto inline-flex px-2 py-0.5 bg-red-200 text-red-800 text-xs font-bold rounded">
                          Đã xác nhận
                        </span>
                      )}
                    </div>
                    {classifyDiff.note && (
                      <div className="bg-white rounded p-2 border border-red-100">
                        <p className="text-xs text-slate-500 font-semibold mb-1">Lý do:</p>
                        <p className="text-sm text-slate-800 whitespace-pre-wrap">{classifyDiff.note}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )} */}

        {/* Equipment Section */}
        {!equipmentLoading && equipmentSummary.length > 0 && (
          <div className="bg-cyan-50 rounded-lg p-4 mb-4 border border-cyan-200">
            <h4 className="text-sm font-semibold text-cyan-800 mb-3 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
              Thiết bị đang sử dụng ({equipmentSummary.reduce((sum, g) => sum + g.count, 0)})
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {equipmentSummary.map((group) => (
                <div key={group.type} className="bg-white rounded-lg p-3 border border-cyan-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-slate-700">
                      {EQUIPMENT_TYPE_NAME[group.type] || group.type_name || `Loại ${group.type}`}
                    </span>
                    <span className="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded-full font-semibold">
                      {group.count}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {group.codes.map((code, i) => (
                      <span key={i} className="text-[11px] bg-slate-50 border border-slate-200 px-2 py-0.5 rounded text-slate-600 font-mono">
                        {code}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {equipmentLoading && (
          <div className="bg-cyan-50 rounded-lg p-4 mb-4 border border-cyan-200">
            <div className="text-xs text-cyan-600 text-center py-2">Đang tải thiết bị...</div>
          </div>
        )}

        {/* Order Issues Section */}
        {!issuesLoading && orderIssues.length > 0 && (
          <div className="bg-orange-50 rounded-lg p-4 mb-4 border border-orange-300">
            <h4 className="text-sm font-semibold text-orange-800 mb-3 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              Vấn đề đã báo ({orderIssues.length})
            </h4>
            <div className="space-y-3">
              {orderIssues.map((issue: any) => (
                <div key={issue.id} className="bg-white rounded-lg p-3 border border-orange-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-800">
                      {issue.employee_name || `NV #${issue.employee_id}`}
                    </span>
                    <div className="flex items-center gap-2">
                      {issue.status_at_report && (
                        <span className="text-[11px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                          {ORDER_STATUS_LABELS[Number(issue.status_at_report)] || `TT ${issue.status_at_report}`}
                        </span>
                      )}
                      <span className="text-xs text-slate-500">
                        {issue.created_at ? (() => {
                          const d = new Date(issue.created_at);
                          const dd = d.getDate().toString().padStart(2, "0");
                          const MM = (d.getMonth() + 1).toString().padStart(2, "0");
                          const yyyy = d.getFullYear();
                          const hh = d.getHours().toString().padStart(2, "0");
                          const mm = d.getMinutes().toString().padStart(2, "0");
                          return `${dd}/${MM}/${yyyy} ${hh}:${mm}`;
                        })() : "—"}
                      </span>
                    </div>
                  </div>
                  {issue.note && (
                    <p className="text-sm text-slate-700 mb-2">{issue.note}</p>
                  )}
                  {issue.images && issue.images.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {issue.images.map((img: string, idx: number) => (
                        <ImageCustom
                          key={idx}
                          url={img}
                          isLocal={false}
                          isUser={false}
                          isReview={true}
                          className="w-20 h-20 object-cover rounded-lg border border-orange-200 cursor-pointer hover:opacity-90 transition"
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {issuesLoading && (
          <div className="bg-orange-50 rounded-lg p-4 mb-4 border border-orange-300">
            <div className="text-xs text-orange-600 text-center py-2">Đang tải vấn đề...</div>
          </div>
        )}

        {/* Sub-orders Section */}
        {order.has_sub_orders === 1 && order.sub_orders && order.sub_orders.length > 0 && (
          <div className="bg-amber-50 rounded-lg p-4 mb-4 border border-amber-200">
            <h4 className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Đơn con ({order.sub_orders.length} đơn)
            </h4>
            <p className="text-xs text-amber-700 mb-3">
              Đơn đã được chia thành {order.sub_orders.length} đơn con. Vui lòng thao tác trên từng đơn con. Tất cả đơn con hoàn tất thì đơn gốc mới được hoàn tất.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-amber-700 bg-amber-100/50">
                  <tr>
                    <th className="px-3 py-2 text-left">Mã đơn con</th>
                    <th className="px-3 py-2 text-center">Loại đồ</th>
                    <th className="px-3 py-2 text-center">Số cái</th>
                    <th className="px-3 py-2 text-center">Trạng thái</th>
                    <th className="px-3 py-2 text-center">Cân nặng</th>
                    <th className="px-3 py-2 text-right">Giảm giá</th>
                    <th className="px-3 py-2 text-right">Thành tiền</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-amber-200">
                  {order.sub_orders.map((sub) => (
                    <tr
                      key={sub.id}
                      className={`bg-white/50 hover:bg-amber-100 transition ${onViewSubOrder ? 'cursor-pointer' : ''}`}
                      onClick={() => onViewSubOrder?.(sub.id)}
                    >
                      <td className="px-3 py-2 font-medium text-indigo-600 underline-offset-2 hover:underline">
                        {sub.code}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-700">
                          {sub.classification_category || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {sub.item_count ?? "—"}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-slate-100 text-slate-700">
                          {ORDER_STATUS_LABELS[sub.status] || "Không rõ"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {sub.final_weight ? `${formatKg(sub.final_weight)} kg` : "—"}
                      </td>
                      <td className="px-3 py-2 text-right text-orange-600">
                        {Number(sub.amount_discount) > 0 ? `-${formatCurrency(sub.amount_discount)}` : "—"}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-green-700">
                        {Number(sub.total_amount) > 0 ? formatCurrency(sub.total_amount) : "—"}
                      </td>
                    </tr>
                  ))}
                  {/* Tổng cộng đơn con */}
                  <tr className="bg-amber-100/70 font-semibold text-amber-900">
                    <td colSpan={4} className="px-3 py-2 text-right">Tổng cộng:</td>
                    <td className="px-3 py-2 text-center">
                      {(() => {
                        const totalWeight = order.sub_orders.reduce((sum, s) => sum + (Number(s.final_weight) || 0), 0);
                        return totalWeight > 0 ? `${formatKg(totalWeight)} kg` : "—";
                      })()}
                    </td>
                    <td className="px-3 py-2 text-right text-orange-600">
                      {(() => {
                        const totalDiscount = order.sub_orders.reduce((sum, s) => sum + (Number(s.amount_discount) || 0), 0);
                        return totalDiscount > 0 ? `-${formatCurrency(totalDiscount)}` : "—";
                      })()}
                    </td>
                    <td className="px-3 py-2 text-right text-green-700">
                      {(() => {
                        const totalAmount = order.sub_orders.reduce((sum, s) => sum + (Number(s.total_amount) || 0), 0);
                        return totalAmount > 0 ? formatCurrency(totalAmount) : "—";
                      })()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Nút chia đơn — chỉ hiện khi đơn ở bước chờ giặt (extended), chưa chia, và không phải sub-order */}
        {(order.status === ORDER_STATUS.INSPECTING || order.status === ORDER_STATUS.WAITING_LAUNDRY) && order.workflow_type === 1 && order.has_sub_orders !== 1 && !order.parent_order_id && onSplitOrder && (
          <div className="bg-indigo-50 rounded-lg p-4 mb-4 border border-indigo-200 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-indigo-800">Chia đơn hàng</p>
              <p className="text-xs text-indigo-600 mt-1">Chia đơn hàng lớn thành các đơn hàng con</p>
            </div>
            <button
              onClick={onSplitOrder}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              Chia đơn
            </button>
          </div>
        )}

        {/* Điều chuyển chi nhánh: tạo phiếu / thao tác / timeline vị trí */}
        <OrderTransferSection order={order} onReload={onReload} />

        {/* Nút giặt lại — hiện từ sau bước giặt (WAITING_DRY trở đi).
            Cha–con: giặt lại nằm ở ĐƠN CON (đơn thực giặt/giao) + đơn thường; ẨN ở ĐƠN CHA (has_sub_orders=1 — chỉ là bản ghi tổng). */}
        {([ORDER_STATUS.COMPLETE, ORDER_STATUS.READY_DELIVERY,
           15, 12, 16, 13, 17, 14, // WAITING_DRY, DRYING, WAITING_IRON, IRONING, WAITING_FOLD, FOLDING
        ].includes(order.status)) && order.has_sub_orders !== 1 && (
          <div className="bg-orange-50 rounded-lg p-4 mb-4 border border-orange-200 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-orange-800">Giặt lại</p>
              <p className="text-xs text-orange-600 mt-1">
                {order.open_rewash_order
                  ? `Đơn này đang có phiếu giặt lại ${order.open_rewash_order.code} chưa hoàn tất — gộp đồ vào phiếu đó.`
                  : "Tạo đơn giặt lại (0đ) cho đơn này"}
              </p>
            </div>
            {/* Mỗi đơn gốc chỉ 1 phiếu giặt lại đang chạy (BE chặn ở createReWashOrder) → khoá nút luôn
                thay vì để bấm rồi mới báo lỗi. */}
            <button
              onClick={() => setShowReWash(true)}
              disabled={!!order.open_rewash_order}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-orange-600"
            >
              Giặt lại
            </button>
          </div>
        )}

        {editingPhotoKey !== null && (() => {
          const step = editablePhotoSteps.find((s) => s.key === editingPhotoKey);
          if (!step) return null;
          return (
            <StepPhotosEditModal
              orderId={Number(order.id)}
              orderCode={order.code}
              captionKey={step.key}
              stepLabel={step.label}
              initialUrls={step.urls}
              onClose={() => setEditingPhotoKey(null)}
              onSaved={async () => {
                setEditingPhotoKey(null);
                if (typeof onRefreshDetail === "function") await onRefreshDetail(Number(order.id));
                if (typeof onReload === "function") onReload();
              }}
            />
          );
        })()}
        {showCancelCashDeposit && (
          <CancelCashDepositModal
            orderId={Number(order.id)}
            orderCode={order.code}
            onClose={() => setShowCancelCashDeposit(false)}
            onSaved={async () => {
              setShowCancelCashDeposit(false);
              if (typeof onRefreshDetail === "function") await onRefreshDetail(Number(order.id));
              if (typeof onReload === "function") onReload();
            }}
          />
        )}
        {showEditFinalWeight && (
          <EditFinalWeightModal
            orderId={Number(order.id)}
            orderCode={order.code}
            currentWeight={Number(order.final_weight) || 0}
            onClose={() => setShowEditFinalWeight(false)}
            onSaved={async () => {
              setShowEditFinalWeight(false);
              if (typeof onRefreshDetail === "function") await onRefreshDetail(Number(order.id));
              if (typeof onReload === "function") onReload();
            }}
          />
        )}

        {showChangePayment && (
          <ChangePaymentMethodModal
            orderId={Number(order.id)}
            orderCode={order.code}
            paymentMethod={Number(order.payment_method)}
            onClose={() => setShowChangePayment(false)}
            onSaved={async () => {
              setShowChangePayment(false);
              if (typeof onRefreshDetail === "function") await onRefreshDetail(Number(order.id));
              if (typeof onReload === "function") onReload();
            }}
          />
        )}

        {/* Modal Giặt lại (khách trả NGAY — đồ đã ở shop, miễn phí 0đ) */}
        {showEditItems && (
          <OrderItemsEditModal
            orderId={Number(order.id)}
            orderCode={order.code}
            customerId={Number((order as any).customer_id) || undefined}
            branchId={Number(order.branch_id) || undefined}
            items={items}
            onClose={() => setShowEditItems(false)}
            onSaved={async () => {
              setShowEditItems(false);
              // Nạp lại chi tiết đơn đang mở (bảng tính tiền) rồi mới reload danh sách.
              if (typeof onRefreshDetail === "function") await onRefreshDetail(Number(order.id));
              if (typeof onReload === "function") onReload();
            }}
          />
        )}

        {showReWash && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-[480px] max-w-[95vw] max-h-[90vh] shadow-2xl flex flex-col">
              <h3 className="text-lg font-semibold text-center text-orange-600 px-6 pt-6 pb-4 shrink-0">Giặt lại đơn hàng</h3>
              <div className="space-y-4 overflow-y-auto px-6 pb-2 flex-1">
                {/* Nhóm lý do — CHỈ để thống kê (báo cáo giặt lại group theo nhóm này). Ô mô tả chi tiết
                    bên dưới giữ nguyên, luồng xử lý đơn không đổi. */}
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">
                    Nhóm lý do <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-300 focus:outline-none"
                    value={reWashReasonCode}
                    onChange={(e) => setReWashReasonCode(e.target.value)}
                  >
                    {REWASH_REASON_OPTIONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">
                    Mô tả chi tiết <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-300 focus:outline-none resize-none"
                    rows={2}
                    placeholder="VD: Đồ chưa sạch, còn vết bẩn..."
                    value={reWashReason}
                    onChange={(e) => setReWashReason(e.target.value)}
                  />
                </div>

                {/* Dịch vụ giặt lại — lấy từ đơn gốc, xoá bớt được (đồ giặt lại có thể chỉ thuộc 1 dịch vụ). */}
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">
                    Dịch vụ giặt lại <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-slate-400 mb-1.5">
                    Lấy từ đơn gốc. Xoá dòng không giặt lại (giữ tối thiểu 1), sửa SL cho đúng phần giặt lại.
                  </p>
                  {reWashServiceLines.length === 0 ? (
                    <p className="text-xs text-slate-500 italic">Đơn gốc không có dòng dịch vụ nào.</p>
                  ) : (
                    <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                      {reWashServiceLines.map((l) => (
                        <div key={l.id} className="flex items-center gap-2 px-3 py-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-slate-800 truncate">{l.name}</div>
                            <div className="text-xs text-slate-400">
                              {formatCurrency(l.price)}đ{l.unit ? `/${l.unit}` : ""}
                            </div>
                          </div>
                          <input
                            type="text"
                            inputMode={l.isKg ? "decimal" : "numeric"}
                            className="w-20 border rounded-md px-2 py-1 text-sm text-right focus:ring-2 focus:ring-orange-300 focus:outline-none"
                            value={l.qty}
                            onChange={(e) => setReWashLineQtyValue(l.id, e.target.value, l.isKg)}
                          />
                          <button
                            type="button"
                            disabled={reWashServiceLines.length <= 1}
                            onClick={() => setReWashRemovedLineIds((prev) => [...prev, l.id])}
                            className="text-slate-400 hover:text-rose-500 disabled:opacity-30 disabled:hover:text-slate-400"
                            title="Xoá dịch vụ này"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {reWashRemovedLineIds.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setReWashRemovedLineIds([])}
                      className="text-xs text-orange-600 mt-1 hover:underline"
                    >
                      Khôi phục dịch vụ đã xoá
                    </button>
                  )}
                </div>

                {/* Loại phát hiện */}
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Loại phát hiện</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setReWashNeedPickup(false)}
                      className={`border rounded-lg px-3 py-2 text-sm text-left transition ${!reWashNeedPickup ? "border-orange-500 bg-orange-50 ring-1 ring-orange-300" : "border-slate-200 hover:bg-slate-50"}`}
                    >
                      <div className="font-medium">Phát hiện ngay</div>
                      <div className="text-xs text-slate-500">Đồ ở shop · miễn phí</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => { setReWashNeedPickup(true); setReWashRequiresCounting(Number(order.requires_counting) === 1); }}
                      className={`border rounded-lg px-3 py-2 text-sm text-left transition ${reWashNeedPickup ? "border-orange-500 bg-orange-50 ring-1 ring-orange-300" : "border-slate-200 hover:bg-slate-50"}`}
                    >
                      <div className="font-medium">Phát hiện sau</div>
                      <div className="text-xs text-slate-500">Lấy hàng lại · có thể tính phí</div>
                    </button>
                  </div>
                </div>

                {/* Giảm giá + kiểm đếm (chỉ phát hiện sau) */}
                {reWashNeedPickup && (
                  <div className="space-y-3 border border-slate-200 rounded-lg p-3 bg-slate-50/60">
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-1">Cách thu tiền</label>
                      <div className="flex flex-col gap-1.5 text-sm">
                        {([
                          { v: 0, label: "Đơn 0 đồng (miễn phí)" },
                          { v: 1, label: "Giảm theo % tổng bill" },
                          { v: 2, label: "Giảm theo số tiền" },
                        ] as const).map((opt) => (
                          <label key={opt.v} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="rewash-discount"
                              checked={reWashDiscountType === opt.v}
                              onChange={() => { setReWashDiscountType(opt.v); setReWashDiscountValue(""); }}
                            />
                            <span>{opt.label}</span>
                          </label>
                        ))}
                      </div>
                      {reWashDiscountType !== 0 && (
                        <div className="mt-2 flex items-center gap-2">
                          <input
                            type="text"
                            inputMode="numeric"
                            className="w-40 border rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-orange-300 focus:outline-none"
                            placeholder={reWashDiscountType === 1 ? "Nhập %" : "Nhập số tiền"}
                            value={reWashDiscountValue}
                            onChange={(e) => {
                              const d = e.target.value.replace(/\D/g, "");
                              if (reWashDiscountType === 1) {
                                // %: tối đa 100
                                setReWashDiscountValue(d ? String(Math.min(Number(d), 100)) : "");
                              } else {
                                // số tiền: ngăn cách nghìn
                                setReWashDiscountValue(d ? Number(d).toLocaleString("vi-VN") : "");
                              }
                            }}
                          />
                          <span className="text-sm text-slate-500">{reWashDiscountType === 1 ? "%" : "đ"}</span>
                        </div>
                      )}
                      <p className="text-xs text-slate-400 mt-1">
                        Số tiền được tính trên gross theo số ký cân cuối của đơn giặt lại (chỉ giặt phần lỗi).
                      </p>
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={reWashRequiresCounting}
                        onChange={(e) => setReWashRequiresCounting(e.target.checked)}
                      />
                      <span>Yêu cầu kiểm đếm</span>
                    </label>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">
                    Nhân sự chịu trách nhiệm <span className="text-slate-400 font-normal">(tùy chọn)</span>
                  </label>
                  <SelectDropdownAsyncPaginate<PickupStaff>
                    key={`rewash-resp-${order.branch_id || "all"}`}
                    url={`${API_USER_ROUTER.API_LIST}`}
                    projectId={ApiConfig.PROJECT_ID.USER_SERVICE}
                    placeholder={order.branch_id ? "Chọn nhân sự chịu trách nhiệm..." : "Chọn chi nhánh trước"}
                    isMulti
                    disabled={!order.branch_id}
                    defaultOptions={reWashResponsible}
                    onChange={(selected) => setReWashResponsible(selected as PickupStaff[])}
                    labelOption="name"
                    classNames="w-full"
                    params={{ status: 1, branch_id: order.branch_id || undefined }}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 shrink-0">
                <button
                  onClick={() => setShowReWash(false)}
                  disabled={reWashSubmitting}
                  className="px-4 py-2 border rounded-lg text-slate-700 hover:bg-slate-100 transition"
                >
                  Trở lại
                </button>
                <button
                  onClick={async () => {
                    if (!reWashReason.trim()) { showToast({ message: "Vui lòng nhập lý do giặt lại", type: "warning" }); return; }
                    const reWashDiscountNum = Number(String(reWashDiscountValue).replace(/\D/g, "")) || 0;
                    if (reWashNeedPickup && reWashDiscountType !== 0) {
                      if (!(reWashDiscountNum > 0)) { showToast({ message: reWashDiscountType === 1 ? "Nhập % giảm giá" : "Nhập số tiền giảm", type: "warning" }); return; }
                      if (reWashDiscountType === 1 && reWashDiscountNum > 100) { showToast({ message: "% giảm giá tối đa 100", type: "warning" }); return; }
                    }
                    if (reWashServiceLines.length === 0) { showToast({ message: "Đơn giặt lại phải giữ ít nhất 1 dịch vụ", type: "warning" }); return; }
                    const service_lines = reWashServiceLines.map((l) => ({
                      id: l.id,
                      quantity: Number(String(l.qty).replace(",", ".")) || 0,
                    }));
                    if (service_lines.some((l) => !(l.quantity > 0))) { showToast({ message: "Nhập SL giặt lại (> 0) cho từng dịch vụ giữ lại", type: "warning" }); return; }
                    setReWashSubmitting(true);
                    try {
                      const responsible_user_ids = reWashResponsible.map((s) => s.id).join(",");
                      const res = await reWashRef.current({
                        endPoint: API_ORDER_ROUTER.API_RE_WASH(Number(order.id)),
                        data: {
                          reason: reWashReason.trim(),
                          reason_code: reWashReasonCode,
                          responsible_user_ids: responsible_user_ids || undefined,
                          service_lines,
                          need_pickup: reWashNeedPickup,
                          ...(reWashNeedPickup
                            ? {
                                requires_counting: reWashRequiresCounting ? 1 : 0,
                                discount_type: reWashDiscountType,
                                discount_value: reWashDiscountType !== 0 ? reWashDiscountNum : 0,
                              }
                            : {}),
                        },
                      });
                      if (res && isSuccess(res)) {
                        showToast({
                          message: reWashNeedPickup ? "Đã tạo đơn giặt lại (phát hiện sau) thành công" : "Đã tạo đơn giặt lại (miễn phí) thành công",
                          type: "success",
                        });
                        setShowReWash(false);
                        if (typeof onReload === "function") onReload(); // reload danh sách đơn
                        if (typeof onClose === "function") onClose();   // đóng modal chi tiết
                      } else {
                        showToast({ message: res?.message || "Lỗi tạo đơn giặt lại", type: "error" });
                      }
                    } catch (err: any) {
                      showToast({ message: err?.message || "Lỗi tạo đơn giặt lại", type: "error" });
                    } finally {
                      setReWashSubmitting(false);
                    }
                  }}
                  disabled={reWashSubmitting || !reWashReason.trim()}
                  className="px-5 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition disabled:opacity-50"
                >
                  {reWashSubmitting ? "Đang tạo..." : "Tạo đơn giặt lại"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Hình CK khi giao hàng */}
        {order.payment_transfer_image && (
          <div className="bg-blue-50 rounded-lg p-4 mb-4 border border-blue-200">
            <p className="text-sm font-medium text-blue-800 mb-2">Hình chuyển khoản</p>
            <img
              src={`${order.payment_transfer_image}`}
              alt="Hình CK"
              className="w-32 h-32 object-cover rounded border cursor-pointer"
              onClick={() => window.open(order.payment_transfer_image, '_blank')}
            />
          </div>
        )}

        </>
        )}

        {activeTab === "process" && (
          <OrderProcessTab
            order={order}
            workflowLogs={workflowLogs}
            transfers={transfers}
            classifyGroups={classifyGroups as any}
            faults={faults}
            faultsLoading={faultsLoading}
            equipmentDetail={equipmentDetail}
          />
        )}

        <div className="mt-5 flex justify-between items-center">
          {/* Tạo phiếu phạt: chỉ cho đơn GIẶT LẠI */}
          {Number(order.order_type) === 2 ? (
            <button
              onClick={() => setShowFaultCreate(true)}
              className="px-4 py-2 border border-red-300 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 transition"
            >
              + Tạo phiếu phạt
            </button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPrint(true)}
              disabled={classifyGroups.length === 0}
              className="px-5 py-2 border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              In hoá đơn
            </button>
            <button
              onClick={onClose}
              className="px-5 py-2 border rounded-lg text-slate-700 hover:bg-slate-100 transition"
            >
              Đóng
            </button>
          </div>
        </div>
        {showPrint && <ReconcilePrintSheet order={order} classifyGroups={classifyGroups as any} />}

        {/* Modal tạo phiếu phạt (tay) — prefill chi nhánh + mã đơn */}
        <FaultTicketCreateModal
          open={showFaultCreate}
          onClose={() => setShowFaultCreate(false)}
          onCreated={() => { setShowFaultCreate(false); setFaultReloadKey((k) => k + 1); }}
          defaultBranchId={order.branch_id ?? null}
          defaultOrderCode={order.code}
        />
      </div>
    </div>
  );
};

export default OrderDetailModal;
